import React, { useEffect, useState } from 'react'
import {
  Box, Button, Card, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { C, RADIUS, SX, TYPE } from '../../merchants/shared/tokens'
import { saveBlob, filenameFromResponse } from '../../freight-profitability/freightPnlUtils'
import { errorText, upload } from '../ptlSetupApi'
import { COUNT_LABEL, ZONE_MODES, num } from '../constants'
import { Banner, Breadcrumb } from '../components/SetupUi'
import PtlPage from '../shell/PtlPage'
import UploadErrors from '../steps/UploadErrors'
import { useSetupTarget } from './targets'

const CHECK_TONE = { removed: 'warn', zoneChanges: 'warn' }

/**
 * Upload of a PTL zone map or TAT (plan screen U), one job: choose (mode +) file → check, nothing saved → see what the
 * upload will do → confirm dialog (pricing warning + tick) → save → back to the screen it came from. The same file is sent
 * twice, so what is saved is exactly what was checked. Any error row rejects the whole file.
 *
 * @param target  key of SETUP_TARGETS
 */
export default function UploadScreen({ target }) {
  const { t, error } = useSetupTarget(target)
  return t ? <UploadBody t={t} /> : <Resolving title='Upload' error={error} />
}

function UploadBody({ t }) {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [now, setNow] = useState(null)
  const [mode, setMode] = useState('UPDATE')
  const [file, setFile] = useState(null)
  const [check, setCheck] = useState(null)
  const [acknowledged, setAcknowledged] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  // a save changes price / EDD at once and has no rollback: the confirm needs this tick
  const [understood, setUnderstood] = useState(false)
  // download being prepared (a big file takes seconds), so its button shows it is working
  const [fetching, setFetching] = useState('')

  useEffect(() => {
    t.now().then(setNow).catch((e) => setError(errorText(e, `Could not load the ${t.noun}`)))
  }, [t])

  const clear = () => { setCheck(null); setError(''); setAcknowledged(false) }
  const back = () => navigate(t.parent.to)

  const getFile = async (d) => {
    setFetching(d.name)
    try {
      const res = await d.get()
      saveBlob(res.data, filenameFromResponse(res, d.name))
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not download the file'), { variant: 'error' })
    } finally {
      setFetching('')
    }
  }
  /** A download's label may depend on the current state (e.g. "Download template" while nothing is set yet). */
  const labelOf = (d) => (typeof d.label === 'function' ? d.label(now) : d.label)

  const run = async (confirm) => {
    setBusy(true)
    setError('')
    try {
      const args = { file, merchantId: t.merchantId, mode: t.modes ? mode : undefined, confirm, acknowledge: confirm && acknowledged }
      const res = await (t.upload ? t.upload(args) : upload(t.kind, args))
      if (!confirm) {
        setCheck(res)
      } else {
        setConfirmOpen(false)
        enqueueSnackbar(t.savedMessage ? t.savedMessage(res) : `${t.label} saved. The change is in its History.`, { variant: 'success' })
        back()
      }
    } catch (e) {
      setConfirmOpen(false)
      setError(errorText(e, 'Upload failed'))
    } finally {
      setBusy(false)
    }
  }

  const errors = check?.errors || []
  const counts = check?.counts || {}
  const ok = check && errors.length === 0
  const needsAck = ok && check.needsAcknowledge

  const kpis = check
    ? [
      { key: 'rows', label: 'Rows in file', value: num(check.rowsInFile), sub: check.fileName },
      ...t.checkKeys.filter((k) => counts[k] !== undefined).map((k) => ({ key: k, label: COUNT_LABEL[k], value: num(counts[k]), tone: counts[k] ? CHECK_TONE[k] : undefined })),
      { key: 'errors', label: 'Rows with errors', value: num(errors.length), tone: errors.length ? 'danger' : 'ok', sub: errors.length ? 'Fix them and check again' : 'File is valid' },
    ]
    : now || [{ key: 'a', label: 'Now', value: '' }, { key: 'b', label: 'Now', value: '' }, { key: 'c', label: 'Last change', value: '' }]

  const modeCard = (key) => {
    const m = ZONE_MODES[key]
    const on = mode === key
    return (
      <Box key={key} component='button' type='button' role='radio' aria-checked={on} onClick={() => { setMode(key); clear() }}
        sx={{
          all: 'unset', boxSizing: 'border-box', cursor: 'pointer', p: 1.75, borderRadius: RADIUS.md,
          border: `1px solid ${on ? C.accent : C.line}`, bgcolor: on ? C.accentSoft : C.surface,
          '&:hover': on ? {} : { borderColor: C.accentBorder }, '&:focus-visible': { outline: `2px solid ${C.accent}`, outlineOffset: 2 },
        }}>
        <Typography component='span' sx={{ ...TYPE.rowLabel, display: 'block' }}>{m.label}</Typography>
        <Typography component='span' sx={{ ...TYPE.rowHelp, display: 'block', mt: 0.25 }}>{m.hint} {key === 'REPLACE' ? t.replaceHint : ''}</Typography>
      </Box>
    )
  }

  return (
    <PtlPage title={`Upload · ${t.label}`} eyebrow={t.eyebrow}
      description={t.description || 'Check the file first: nothing is saved until you confirm. A save changes pricing at once and cannot be rolled back; who saved what is kept in the History.'}
      kpis={kpis} kpisLoading={!check && !now && !error} kpisLabel={check ? 'Upload check' : `${t.label} now`}
      breadcrumb={<Breadcrumb parent={t.parent.label} onParent={back} current='Upload' />}>
      <Card elevation={0} sx={{ ...SX.card, p: 2.5 }}>
        <Box sx={{ display: 'grid', gap: 2.5, maxWidth: 820 }}>
          {t.modes && (
            <Box>
              <Typography sx={{ ...TYPE.rowLabel, mb: 0.75 }}>1. What the file does</Typography>
              <Box role='radiogroup' aria-label='Upload mode' sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                {Object.keys(ZONE_MODES).map(modeCard)}
              </Box>
            </Box>
          )}
          <Box>
            <Typography sx={{ ...TYPE.rowLabel, mb: 0.75 }}>{t.modes ? '2.' : '1.'} File</Typography>
            <Typography sx={{ ...TYPE.rowHelp, mb: 1.25 }}>{t.fileHelp}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>
              <Button component='label' variant='outlined' startIcon={<UploadFileOutlinedIcon />} disabled={busy} sx={{ ...SX.ctaGhost, minHeight: 36 }}>
                {file ? file.name : 'Choose CSV file'}
                <input hidden type='file' accept='.csv' onChange={(e) => { setFile(e.target.files?.[0] || null); clear(); e.target.value = '' }} />
              </Button>
              {t.downloads.map((d) => (
                <LoadingButton key={d.name} loading={fetching === d.name} loadingPosition='start' startIcon={<DownloadOutlinedIcon />} onClick={() => getFile(d)}
                  sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36 }}>
                  {fetching === d.name ? 'Preparing file…' : labelOf(d)}
                </LoadingButton>
              ))}
            </Box>
          </Box>
        </Box>

        {error && <Box sx={{ mt: 2.5 }}><Banner tone='danger'>{error}</Banner></Box>}

        {check && (
          <Box sx={{ mt: 3 }} aria-live='polite'>
            <Typography component='h2' sx={{ ...TYPE.sectionTitle, mb: 1.25 }}>
              {ok ? 'The file is valid. The cards above show what saving it will do.' : 'The file was rejected. Nothing was saved.'}
            </Typography>
            <UploadErrors errors={errors} keyLabel={t.keyLabel} fileName={`ptl_${t.kind}_upload_errors.csv`} />
            {ok && (check.warnings || []).map((w) => <Banner key={w} tone='warn'>{w}</Banner>)}
            {needsAck && (
              <FormControlLabel control={<Checkbox size='small' checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
                label={<Typography sx={{ fontSize: 13, color: C.inkBody }}>I have checked the file and want to apply it anyway</Typography>} />
            )}
            {ok && <Typography sx={{ ...TYPE.rowHelp, mt: 1 }}>{t.saveNote || 'Saving applies to shipments priced from then on; existing shipments keep their zone and EDD. There is no rollback: to undo it, upload the corrected file.'}</Typography>}
          </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1, mt: 3, pt: 2, borderTop: `1px solid ${C.line}` }}>
          <Button onClick={back} disabled={busy} sx={{ textTransform: 'none', fontWeight: 600, color: C.inkMuted }}>Cancel</Button>
          {!ok ? (
            <LoadingButton variant='contained' loading={busy} disabled={!file} onClick={() => run(false)} sx={{ ...SX.cta, minHeight: 36 }}>
              {check ? 'Check again' : 'Check file'}
            </LoadingButton>
          ) : (
            <Button variant='contained' disabled={busy || (needsAck && !acknowledged)} onClick={() => { setUnderstood(false); setConfirmOpen(true) }} sx={{ ...SX.cta, minHeight: 36 }}>
              Save {t.noun}
            </Button>
          )}
        </Box>
      </Card>

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>Save the {t.noun}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...TYPE.rowHelp, fontSize: 13.5, color: C.inkBody }}>
            {t.checkKeys.filter((k) => counts[k]).map((k) => `${COUNT_LABEL[k]}: ${num(counts[k])}`).join(' · ') || 'No row changes.'}
          </Typography>
          {t.modes && mode === 'REPLACE' && (
            <Typography sx={{ ...TYPE.rowHelp, fontSize: 13.5, color: C.inkBody, mt: 1 }}>Replace full map: {t.replaceHint}</Typography>
          )}
          <Box sx={{ mt: 1.5 }}>
            <Banner tone='warn'>{t.effect} This changes pricing at once and cannot be rolled back: to undo it, upload the corrected file.</Banner>
          </Box>
          <FormControlLabel sx={{ mt: 0.5 }}
            control={<Checkbox size='small' checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13, color: C.inkBody }}>I understand this changes pricing and cannot be rolled back</Typography>} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy} sx={{ textTransform: 'none', color: C.inkMuted }}>Cancel</Button>
          {/* Replacing the map takes the missing pincodes out of service: the button says it is destructive. */}
          <LoadingButton variant='contained' loading={busy} disabled={!understood} onClick={() => run(true)}
            sx={t.modes && mode === 'REPLACE' && counts.removed ? { ...SX.cta, bgcolor: C.danger, '&:hover': { bgcolor: C.danger, boxShadow: 'none' } } : SX.cta}>
            {t.modes && mode === 'REPLACE' && counts.removed ? `Save and move ${num(counts.removed)} ${t.removedTo}` : 'Save'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </PtlPage>
  )
}

/** Loading / error frame while a merchant target is resolved. */
function Resolving({ title, error }) {
  return (
    <PtlPage title={title}>
      {error ? <Banner tone='danger'>{error}</Banner> : <Typography role='status' sx={{ ...TYPE.rowHelp, py: 4, textAlign: 'center' }}>Loading…</Typography>}
    </PtlPage>
  )
}
